import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { StyledButton } from "@components/Styled/Button";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { css } from "@emotion/react";
import { Link } from "gatsby";
import React, { lazy, Suspense } from "react";
import SwiperCore, {
  A11y,
  Autoplay,
  Keyboard,
  Mousewheel,
  Navigation,
  Scrollbar,
} from "swiper";
import { SwiperSlide } from "swiper/react";
import tw, { styled } from "twin.macro";
import FeaturedCollectionCard from "./FeaturedCollectionCard";
import { withFeaturedCollectionCarousel } from "./withFeaturedCollectionCarousel";

const Swiper = lazy(
  () => import(/* webpackChunkName: "swiper" */ "@components/Swiper/Swiper"),
);

SwiperCore.use([Autoplay, A11y, Keyboard, Mousewheel, Navigation, Scrollbar]);

type StyledArrowProps = {
  id: string;
  direction: string;
  count: number;
};

const Wrapper = withFocusRing(
  styledWithInvalidProps("div")(({ autoDelay }) => [
    autoDelay &&
      css`
        .swiper-wrapper {
          transition-timing-function: linear !important;
        }
      `,
  ]),
);
const Desktop = tw.div`
  hidden justify-end pr-midi md:flex xl:pr-tera
`;
const Mobile = tw.div`
  block mt-[18px] md:hidden
`;
const StyledLink = withFocusRing(
  tw(Link)`text-body underline leading-caption hover:text-secondary`,
);
const Button = tw(StyledButton)`
  mx-midi mt-midi
`;
const ArrowWrapper = tw.div`
  flex items-center ml-midi md:ml-mini
`;
const StyledArrow = withFocusRing(
  styledWithInvalidProps<StyledArrowProps>("div")(({ direction }) => [
    tw`!inline-flex cursor-pointer text-primary hover:opacity-70`,
    direction === "prev" ? tw`transform rotate-180 mr-mini` : null,
  ]),
);
const StyledSwiper = styled(Swiper)`
  ${tw`mt-maxi`}
  ${css`
    .swiper-initialized {
      ${tw`mt-maxi pl-midi xl:pl-tera`}
    }
    .swiper-slide:focus-visible,
    .group:focus-visible {
      outline-style: dashed;
      outline-width: 1px;
      outline-offset: -1px;
      outline-color: currentColor;
    }
  `}
`;

const SliderArrow = ({ id, direction, count }: StyledArrowProps) => (
  <StyledArrow id={`${direction}-${id}`} direction={direction} count={count}>
    <Icon name="vintageArrow" size="xsmall" />
  </StyledArrow>
);

export const FeaturedCollectionCarousel = withFeaturedCollectionCarousel(
  ({
    activeTab,
    autoDelay,
    collectionContents,
    changeDirection,
    handleClick,
    isMD,
    _key,
    setSwiper,
  }) => (
    <Wrapper
      autoDelay={!!autoDelay}
      role="tabpanel"
      aria-labelledby={`${activeTab?.toLowerCase()}_${_key}`}
    >
      {collectionContents?.map(({ link, contents }) => (
        <>
          <Desktop>
            {link?.url && (
              <StyledLink
                onClick={() => handleClick(link?.title)}
                to={link?.url}
                tabIndex={0}
              >
                {link?.title}
              </StyledLink>
            )}

            {!autoDelay ? (
              <ArrowWrapper>
                <SliderArrow
                  id={`featured-collections-${_key}`}
                  direction="prev"
                  count={contents?.length}
                />
                <SliderArrow
                  id={`featured-collections-${_key}`}
                  direction="next"
                  count={contents?.length}
                />
              </ArrowWrapper>
            ) : null}
          </Desktop>

          <Suspense fallback={<></>}>
            <StyledSwiper
              speed={autoDelay && Number(autoDelay)}
              loop={autoDelay ? true : false}
              autoplay={
                autoDelay
                  ? {
                      delay: 0,
                      disableOnInteraction: false,
                    }
                  : { enabled: false }
              }
              onSwiper={setSwiper}
              onSlideChange={changeDirection}
              navigation={
                !autoDelay
                  ? {
                      prevEl: `#prev-featured-collections-${_key}`,
                      nextEl: `#next-featured-collections-${_key}`,
                    }
                  : { nextEl: null, prevEl: null }
              }
              keyboard={{
                enabled: true,
              }}
              mousewheel={{
                enabled: true,
                forceToAxis: true,
              }}
              a11y={{
                enabled: true,
                prevSlideMessage: "Previous slide",
                nextSlideMessage: "Next slide",
                firstSlideMessage: "This is the first slide",
                lastSlideMessage: "This is the last slide",
                slideRole: "group",
              }}
              key={contents?.length}
              scrollbar={!isMD && { draggable: true }}
              slidesPerView={1.5}
              spaceBetween={0}
              lazy={true}
              preloadImages={false}
              observer={true}
              observeParents={true}
              watchOverflow={true}
              breakpoints={{
                415: {
                  slidesPerView: 2.5,
                  spaceBetween: 0,
                  observer: true,
                  observeParents: true,
                },
                769: {
                  slidesPerView: 3.5,
                  spaceBetween: 0,
                  observer: true,
                  observeParents: true,
                },
                1025: {
                  slidesPerView: 4.5,
                  spaceBetween: 0,
                  observer: true,
                  observeParents: true,
                },
              }}
            >
              {contents?.map((content) => (
                <SwiperSlide key={content?._key} tabIndex={0}>
                  <FeaturedCollectionCard
                    image={content?.image}
                    link={content?.link}
                    handlePromoClick={handleClick}
                  />
                </SwiperSlide>
              ))}
            </StyledSwiper>
          </Suspense>

          <Mobile>
            {link?.url && (
              <Button
                height="56"
                textSize="16"
                colour="transparent"
                border
                onClick={() => handleClick(link?.title)}
                as={Link}
                to={link?.url}
              >
                {link?.title}
              </Button>
            )}
          </Mobile>
        </>
      ))}
    </Wrapper>
  ),
);
